import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { transformQuery, isRuleOrGroupValid } from 'react-querybuilder'
import { ReportingContext } from '../../../../wrappers/ReportingContext'
import CollapsibleContainer from '../CollapsibleContainer'
import { Controller, useFormContext } from 'react-hook-form'
import DefaultQueryBuilder from '../form/filters/DefaultQueryBuilder'
import ConfirmationModal from '../../../../features/reporting/ConfirmationModal'
import { ruleValidation } from '../../../../../utils/reporting/reportValidations'

// const formatDataSourceName = (dataSource, dataSourceColumn) => {
//     const dataSourceName = dataSource.alias ? dataSource.alias : dataSource.name
//     const dataSourceColumnName = dataSourceColumn.alias
//         ? dataSourceColumn.alias
//         : dataSourceColumn.name
//     return `${dataSourceName}.${dataSourceColumnName}`
// }

const getInputType = (dataType) => {
    switch (dataType) {
        case 'int':
        case 'bigint':
        case 'smallint':
        case 'tinyint':
        case 'mediumint':
        case 'integer':
        case 'number':
        case 'numeric':
        case 'decimal':
        case 'float':
        case 'double':
        case 'real':
        case 'serial':
        case 'bigserial':
            return 'number'
        case 'date':
        case 'datetime':
        case 'datetime2':
        case 'datetimeoffset':
        case 'smalldatetime':
        case 'time':
        case 'timestamp':
        case 'year':
            return 'date'
        case 'char':
        case 'varchar':
        case 'text':
        case 'tinytext':
        case 'mediumtext':
        case 'longtext':
        case 'enum':
        case 'set':
            return 'text'
        case 'binary':
        case 'varbinary':
        case 'blob':
        case 'tinyblob':
        case 'mediumblob':
        case 'longblob':
            return 'file'
        case 'bit':
        case 'bool':
        case 'boolean':
            return 'boolean'
        default:
            return 'text'
    }
}

// const ruleShouldValidate = () => { //returns true if should validate, false if not
//     return inputType !== 'boolean' //

// }

// const operators = [
//     { name: '=', label: '=' },
//     { name: '!=', label: '!=' },
//     { name: '<', label: '<' },
//     { name: '>', label: '>' },
//     { name: '<=', label: '<=' },
//     { name: '>=', label: '>=' },
//     { name: 'contains', label: 'contains' },
//     { name: 'beginsWith', label: 'begins with' },
//     { name: 'endsWith', label: 'ends with' },
//     { name: 'doesNotContain', label: 'does not contain' },
//     { name: 'doesNotBeginWith', label: 'does not begin with' },
//     { name: 'doesNotEndWith', label: 'does not end with' },
//     { name: 'null', label: 'is null' },
//     { name: 'notNull', label: 'is not null' },
//     { name: 'in', label: 'in' },
//     { name: 'notIn', label: 'not in' },
//     { name: 'between', label: 'between' },
//     { name: 'notBetween', label: 'not between' },
// ]

const defaultFilter = {
    not: false,
    rules: [],
}

const IncludeGroupsButton = ({ groupsIncluded, setGroupsIncluded, query }) => {
    const { setValue, register } = useFormContext()
    const [displayGroupsConfirmation, setDisplayGroupsConfirmation] =
        useState(false)
    const rules = useMemo(() => (query && query.rules) || [], [query])
    // useEffect(() => {
    //     register('whereGroupsIncluded')
    // }, [register])
    // useEffect(() => {
    //     setValue('whereGroupsIncluded', groupsIncluded) //, { shouldValidate: false, shouldDirty: false, shouldTouch: false }) these options are not available in this version, uncomment after library upgrade
    // }, [groupsIncluded, setValue])

    return (
        <div className="includeGroups">
            <input
                name={`whereGroupsIncluded`}
                ref={register()}
                type="text"
                className={`collapsedField`}
                defaultValue={groupsIncluded || false}
                value={groupsIncluded}
            />
            <button
                type="button"
                className={`button is-link`}
                onClick={() => {
                    if (rules && rules.length) {
                        setDisplayGroupsConfirmation(true)
                    } else {
                        setGroupsIncluded((prev) => !prev)
                    }
                }}
            >
                {groupsIncluded ? `Exclude Groups` : `Include Groups`}
            </button>
            <ConfirmationModal
                display={displayGroupsConfirmation}
                title={'Change Groups Option Confirmation'}
                confirmAction={() => {
                    setValue('filters', defaultFilter)
                    setGroupsIncluded((prev) => !prev)
                    setDisplayGroupsConfirmation(false)
                }}
                confirmText={`Continue`}
                denyAction={() => setDisplayGroupsConfirmation(false)}
                denyText={`Cancel`}
            >
                <p>
                    Changing the Groups option will reset the screen and all
                    existing filters will be removed. Are you sure you want to
                    continue?
                </p>
            </ConfirmationModal>
        </div>
    )
}

const FilterBuilder = () => {
    const { reportConfig, reportSelectOptions, viewOnly } =
        useContext(ReportingContext)
    const { control, watch, setError } = useFormContext()
    const selectedDataSourceColumns = useMemo(() => {
        return reportSelectOptions
            ? reportSelectOptions.selectedDataSourceColumns
            : []
    }, [reportSelectOptions])

    const selectedDataSources = useMemo(() => {
        return reportSelectOptions && reportSelectOptions.selectedDataSources
            ? reportSelectOptions.selectedDataSources
            : []
    }, [reportSelectOptions])

    // const dataSourceOptions = useMemo(() => {
    //     return reportSelectOptions && reportSelectOptions.dataSources
    //         ? reportSelectOptions.dataSources
    //         : []
    // }, [reportSelectOptions])

    const fields = useMemo(() => {
        let fieldList = []
        selectedDataSources.forEach((ds) => {
            fieldList.push(
                ...selectedDataSourceColumns
                    .filter((column) => ds.value === column.TABLE_NAME)
                    .map((column) => {
                        const inputType = getInputType(column.DATA_TYPE)
                        return {
                            name: `${ds.label}.${column.value}`,
                            label: `${ds.label}.${column.label}`,
                            type: column.DATA_TYPE,
                            inputType: inputType,
                            maxLength: column.CHARACTER_MAXIMUM_LENGTH,
                            numericPrecision: column.NUMERIC_PRECISION,
                            numericScale: column.NUMERIC_SCALE,
                            dataTypeDisplay: column.DATA_TYPE_DISPLAY,
                            validator: (rule) =>
                                ruleValidation(
                                    rule,
                                    inputType,
                                    column.DATA_TYPE,
                                    column.NUMERIC_PRECISION,
                                    column.NUMERIC_SCALE
                                ),
                        }
                    })
            )
        })
        return fieldList
    }, [selectedDataSourceColumns, selectedDataSources])

    const ruleProcessor = useCallback(
        (rule) => {
            try {
                if (rule && rule.field && rule.field.includes('.')) {
                    const [dataSourceName, columnName] = rule.field.split('.')

                    const dataSource = selectedDataSources.find(
                        (x) => dataSourceName === (x.alias || x.value)
                    )
                    const dataSourceColumn = selectedDataSourceColumns.find(
                        (x) =>
                            x.COLUMN_NAME === columnName &&
                            x.TABLE_NAME === dataSource.value
                    )
                    const inputType = getInputType(dataSourceColumn?.DATA_TYPE)
                    const validation = ruleValidation(
                        rule,
                        inputType,
                        dataSourceColumn?.DATA_TYPE,
                        dataSourceColumn?.NUMERIC_PRECISION,
                        dataSourceColumn?.NUMERIC_SCALE
                    )

                    return {
                        ...rule,
                        value: String(rule.value), // to do JSON Stringify on form Save all values have to be string
                        table: dataSource?.value,
                        tableAlias: dataSource?.alias,
                        column: dataSourceColumn?.COLUMN_NAME,
                        isValid: isRuleOrGroupValid(rule, validation),
                        validation: validation,
                    }
                }
                return rule
            } catch (e) {
                console.error('Error processing rule:', e)
                return rule
            }
        },
        [selectedDataSources, selectedDataSourceColumns]
    )

    const extractQueryErrors = useCallback((transformedQuery) => {
        const errors = []

        const processRules = (rules) => {
            rules.forEach((rule) => {
                if (rule.rules) {
                    processRules(rule.rules)
                } else if (rule.validation) {
                    if (!rule.validation.valid) {
                        errors.push(rule.validation)
                    }
                }
            })
        }

        if (transformedQuery && transformedQuery.rules) {
            processRules(transformedQuery.rules)
        }

        return errors
    }, [])

    const filters = watch('filters')

    const transformedQuery = useMemo(() => {
        if (!filters) {
            return reportConfig.filters && reportConfig.filters.rules
                ? reportConfig.filters
                : defaultFilter
        }

        return transformQuery(filters, { ruleProcessor })
    }, [filters, ruleProcessor, reportConfig.filters])

    const queryErrors = useMemo(() => {
        return extractQueryErrors(transformedQuery)
    }, [transformedQuery, extractQueryErrors])

    useEffect(() => {
        const hasInvalidRules = queryErrors.some((error) => !error.valid)
        if (hasInvalidRules) {
            setError(
                'filters',
                'required',
                'All fields in the Where Clause tab are required'
            )
        }
    }, [queryErrors, setError])

    const [groupsIncluded, setGroupsIncluded] = useState(false)
    useEffect(() => {
        setGroupsIncluded(reportConfig.whereGroupsIncluded === 'true')
    }, [reportConfig])

    const validateFilters = (value) => {
        const validatedQuery = transformQuery(value, { ruleProcessor })
        const hasInvalidRules = validatedQuery?.rules?.some(
            (rule) => typeof rule === 'object' && rule?.isValid === false
        )

        return hasInvalidRules
            ? 'All fields in the Where Clause tab are required'
            : true
    }

    return (
        <div className="content">
            <IncludeGroupsButton
                groupsIncluded={groupsIncluded}
                setGroupsIncluded={setGroupsIncluded}
                query={transformedQuery}
            />
            <Controller
                as={
                    <DefaultQueryBuilder
                        groupsIncluded={groupsIncluded}
                        // context={queryBuilderContext}
                    />
                }
                name={'filters'}
                control={control}
                onChangeName="onQueryChange"
                // onChange={onChange}
                viewOnly={viewOnly}
                query={transformedQuery}
                fields={fields}
                // operators={operators}
                rules={{ validate: validateFilters }}
            />
        </div>
    )
}

const Filters = () => {
    return (
        <CollapsibleContainer title="Where Clause">
            <FilterBuilder />
        </CollapsibleContainer>
    )
}

export default Filters
